import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Check, ShoppingCart, Minus, Plus, Truck, ShieldCheck, Gift, ArrowLeft, Phone, Loader2,
} from "lucide-react";
import { getProduct, getProducts, fmtPrice, PHONE, PHONE_TEL } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { ProductCard } from "@/components/ProductCard";
import { TrustBand } from "@/components/TrustBand";
import { Reviews } from "@/components/Reviews";
import { Stars } from "@/components/Stars";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [qty, setQty] = useState(1);

  const { data: product, isLoading, isError } = useQuery({
    queryKey: ["product", slug],
    queryFn: () => getProduct(slug),
  });
  const { data: allProducts = [] } = useQuery({ queryKey: ["products"], queryFn: getProducts });

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-eco" />
      </div>
    );
  }

  if (isError || !product) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="font-display font-black text-navy text-2xl">Produs inexistent</h1>
        <Link to="/produse" className="inline-block mt-6 bg-eco hover:bg-green-700 text-white font-bold px-6 py-3 rounded-xl transition-colors">
          Înapoi la produse
        </Link>
      </div>
    );
  }

  const related = allProducts.filter((p) => p.slug !== product.slug).slice(0, 3);

  const buyNow = () => {
    addItem(product, qty);
    navigate("/checkout");
  };

  return (
    <div data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/produse" className="inline-flex items-center gap-2 text-sm font-bold text-navy hover:text-eco mb-6" data-testid="back-to-products">
          <ArrowLeft className="h-4 w-4" /> Înapoi la produse
        </Link>

        <div className="grid lg:grid-cols-2 gap-10">
          {/* Image */}
          <div className="relative bg-slate-50 rounded-2xl border-2 border-slate-100 p-6 flex items-center justify-center">
            {product.badge && (
              <span className="absolute top-4 left-4 bg-red-600 text-white text-xs font-extrabold px-3 py-1 rounded">
                {product.badge}
              </span>
            )}
            <img
              src={product.image}
              alt={product.name}
              className="max-h-[460px] w-full object-contain"
              data-testid="product-detail-image"
            />
          </div>

          {/* Info */}
          <div>
            <h1 className="font-display font-black text-navy text-3xl md:text-4xl tracking-tight" data-testid="product-detail-name">
              {product.name}
            </h1>
            <p className="text-muted-foreground mt-2">{product.description}</p>
            {product.review_count > 0 && (
              <div className="mt-3"><Stars value={product.avg_rating} showValue count={product.review_count} /></div>
            )}

            <div className="flex items-baseline gap-3 mt-5">
              {product.old_price && (
                <span className="text-lg text-slate-400 line-through">{fmtPrice(product.old_price)} lei</span>
              )}
              <span className="font-display font-black text-navy text-4xl" data-testid="product-detail-price">
                {fmtPrice(product.price)}
              </span>
              <span className="font-display font-bold text-navy text-lg">LEI</span>
              <span className="text-sm text-muted-foreground">/ {product.volume}</span>
            </div>

            <ul className="space-y-2 mt-6">
              {product.features.map((f) => (
                <li key={f} className="flex items-center gap-2.5 text-slate-700">
                  <span className="bg-eco/10 rounded-full p-1"><Check className="h-4 w-4 text-eco" /></span>
                  {f}
                </li>
              ))}
            </ul>

            {/* Quantity + actions */}
            <div className="flex flex-wrap items-center gap-4 mt-7">
              <div className="flex items-center border-2 border-slate-200 rounded-xl">
                <button className="p-3" onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="detail-qty-dec">
                  <Minus className="h-4 w-4" />
                </button>
                <span className="w-10 text-center font-bold" data-testid="detail-qty">{qty}</span>
                <button className="p-3" onClick={() => setQty((q) => q + 1)} data-testid="detail-qty-inc">
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              <button
                onClick={() => addItem(product, qty)}
                className="flex-1 min-w-[180px] bg-navy hover:bg-navy-700 text-white font-display font-bold py-3.5 px-6 rounded-xl flex items-center justify-center gap-2 transition-colors"
                data-testid="detail-add-to-cart"
              >
                <ShoppingCart className="h-5 w-5" /> ADAUGĂ ÎN COȘ
              </button>
              <button
                onClick={buyNow}
                className="flex-1 min-w-[180px] bg-eco hover:bg-green-700 text-white font-display font-black py-3.5 px-6 rounded-xl transition-colors"
                data-testid="detail-buy-now"
              >
                COMANDĂ ACUM
              </button>
            </div>

            {/* Guarantees */}
            <div className="grid sm:grid-cols-3 gap-3 mt-7">
              {[
                { icon: Truck, t: "Transport gratuit", s: "la orice comandă" },
                { icon: Gift, t: "2 produse cadou", s: "la fiecare comandă" },
                { icon: ShieldCheck, t: "Plata la livrare", s: "ramburs, 1-2 zile" },
              ].map(({ icon: Icon, t, s }) => (
                <div key={t} className="flex items-center gap-2.5 bg-slate-50 rounded-xl p-3">
                  <Icon className="h-6 w-6 text-eco shrink-0" />
                  <div>
                    <p className="font-bold text-navy text-sm leading-tight">{t}</p>
                    <p className="text-xs text-muted-foreground">{s}</p>
                  </div>
                </div>
              ))}
            </div>

            <a href={PHONE_TEL} className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-navy hover:text-eco">
              <Phone className="h-4 w-4 text-eco" /> Comandă telefonic: {PHONE}
            </a>
          </div>
        </div>

        {/* Description block */}
        <div className="mt-14 bg-slate-50 rounded-2xl p-6 sm:p-8">
          <h2 className="font-display font-black text-navy text-xl mb-3">Detalii produs</h2>
          <p className="text-slate-700 leading-relaxed">
            {product.name} este un {product.description.toLowerCase()} de la DeterShop, cu formulă concentrată
            pentru rezultate impecabile. Un singur bidon oferă până la 400 de spălări, la un cost de doar 0,33 lei / spălare.
            Potrivit pentru albe și colorate, protejează țesăturile și lasă un miros de lungă durată. Fabricat în România.
          </p>
        </div>

        {/* Reviews */}
        <Reviews slug={product.slug} />

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-14">
            <h2 className="font-display font-black text-navy text-2xl md:text-3xl tracking-tight mb-8">
              S-ar putea să îți placă și
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
              {related.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        )}
      </div>

      <TrustBand />
    </div>
  );
}
